import React, { useRef, useState } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'

/**
 * The file door: a CSV or Excel export becomes a receipt plus one record per
 * row, in this job and in no pipeline.
 *
 * It is deliberately NOT the old batch-import wizard — that one created a
 * pipeline per row of a spreadsheet of URLs. This is the plain reading of a
 * file: what is in it becomes data the job has.
 *
 * The panel only chooses the file; the dialog owns the submit, because the
 * three sources share one "add" button and one in-flight gate.
 */

const ACCEPTED = '.csv,.tsv,.xlsx,.xls'

interface CsvUploadPanelProps {
  file: File | null
  onChange: (file: File | null) => void
  disabled?: boolean
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const CsvUploadPanel: React.FC<CsvUploadPanelProps> = ({ file, onChange, disabled = false }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const take = (files: FileList | null) => {
    if (files && files.length > 0) onChange(files[0])
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Upload a file of records
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        CSV, TSV or Excel. Each row becomes one record in this job. Rows this job has already seen
        are recognised as duplicates and not added twice.
      </Typography>

      <Box
        onDragOver={e => {
          e.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault()
          setDragging(false)
          if (!disabled) take(e.dataTransfer.files)
        }}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor: dragging ? 'info.main' : 'divider',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
        <Stack spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Drop a file here, or
          </Typography>
          <Button
            variant="outlined"
            color="info"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            data-testid="autocoder-csv-choose-file"
          >
            Choose file
          </Button>
          {/* The hidden input is what a test sets files on — Playwright's
              setInputFiles works on a hidden input, and going through the
              button would need a real file chooser. */}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            hidden
            data-testid="autocoder-csv-file-input"
            onChange={e => {
              take(e.target.files)
              // Let the same file be chosen twice in a row (a re-upload after a
              // failed parse is a normal thing to want).
              e.target.value = ''
            }}
          />
        </Stack>
      </Box>

      {file && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={disabled ? undefined : () => onChange(null)}>
          <Typography variant="body2">
            {file.name} · {formatSize(file.size)}
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

export default CsvUploadPanel
